/**
 * A mail's verification code while it lasts (core mailCodeView, docs/features/mail.md): the card over the thread with
 * Copy, or only a warning when there's nothing safe to copy, and the compact chip on a row. Both leave when the code
 * runs out. `onCopy` writes the clipboard; nothing here keeps or reports the code.
 */
import { useEffect, useState } from "react";
import type { MailCodeView } from "@bundle/core/logic/mail";
import { Button, Icon, Pressable, Stack, Text } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";

const COPIED_MS = 2000;

/** False from the moment `expiresAt` passes. */
function useInTime(expiresAt: number): boolean {
  const [ranOut, setRanOut] = useState<number | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setRanOut(expiresAt), Math.max(0, expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [expiresAt]);
  return ranOut !== expiresAt;
}

function useCopy(text: string, onCopy: (text: string) => Promise<void> | void): [boolean, () => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  return [copied, () => void Promise.resolve(onCopy(text)).then(() => setCopied(true), () => undefined)];
}

export type MailCodeCardProps = {
  view: MailCodeView;
  sender: string;
  /** When the code came, already formatted. */
  time: string;
  onCopy: (text: string) => Promise<void> | void;
  testID: string;
};

export function MailCodeCard({ view, sender, time, onCopy, testID }: MailCodeCardProps) {
  const t = useT();
  const inTime = useInTime(view.expiresAt);
  const [copied, copy] = useCopy(view.copyText, onCopy);
  if (!inTime) return null;
  if (view.code === null) {
    return (
      <Stack direction="horizontal" align="center" gap="2" padding="3" radius="lg" background="status.danger.bg" border="status.danger.border" testID={testID}>
        <Icon name="warning" size="icon.sm" color="status.danger.fg" />
        <Stack grow>
          <Text variant="footnote" color="status.danger.fg">
            {t("mail.code.unverified")}
          </Text>
        </Stack>
      </Stack>
    );
  }
  return (
    <Stack
      direction="horizontal"
      align="center"
      gap="3"
      padding="3"
      radius="lg"
      background={view.spam ? "status.warning.bg" : "bg.surface"}
      border={view.spam ? "status.warning.border" : "border.subtle"}
      testID={testID}
    >
      <Stack grow gap="0.5">
        <Text variant="footnote" color={view.spam ? "status.warning.fg" : "text.secondary"}>
          {view.spam ? t("mail.code.spam", { sender }) : t("mail.code.from", { sender, time })}
        </Text>
        <Text variant="title2" weight="semibold" mono selectable testID={`${testID}-value`}>
          {view.code}
        </Text>
      </Stack>
      <Button label={t(copied ? "mail.code.copied" : "mail.code.copy")} icon={copied ? "check" : "copy"} size="sm" onPress={copy} testID={`${testID}-copy`} />
    </Stack>
  );
}

export type MailCodeChipProps = {
  code: string;
  expiresAt: number;
  onCopy: (text: string) => Promise<void> | void;
  testID: string;
};

/** The code on a row: pressing it copies, the row around it still opens. */
export function MailCodeChip({ code, expiresAt, onCopy, testID }: MailCodeChipProps) {
  const t = useT();
  const inTime = useInTime(expiresAt);
  const [copied, copy] = useCopy(code, onCopy);
  if (!inTime) return null;
  return (
    <Pressable direction="horizontal" radius="md" onPress={copy} accessibilityLabel={t("mail.code.copyNamed", { code })} testID={testID}>
      <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" radius="md" background="bg.elevated" border="border.subtle">
        <Text variant="footnote" weight="semibold" mono lines={1}>
          {code}
        </Text>
        <Icon name={copied ? "check" : "copy"} size="icon.sm" color="text.secondary" />
      </Stack>
    </Pressable>
  );
}
